<template>
  <div class="lg:!mx-0 flex">
    <div class="flex flex-col max-h-content px-6 pb-2.5 min-h-content space-y-2.5 w-screen">
      <!-- Header Section -->
      <!-- <div class="flex items-stretch justify-between  element-island">
        <div class="flex items-center space-x-3">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              {{ $t('HeadContract.CostAggregationDashboard.title') }}
            </h1>

            <p class="text-sm text-gray-600">
              {{ $t('HeadContract.CostAggregationDashboard.subTitle') }}
            </p>
          </div>
        </div>
      </div> -->

      <div class="flex flex-col w-full overflow-y-auto flex-1 mt-2.5">
        <!-- Top Row - Cost Breakdown and Contract Synopsis -->
        <div class="flex w-full space-x-2 h-1/2">
          <!-- Cost Aggregation Table -->
          <div class="bg-white rounded-xl w-1/2 flex flex-col">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-xl">
              <div class="flex items-center justify-between">
                <h2 class="font-semibold text-white">
                  {{ $t('HeadContract.CostAggregationDashboard.costBreakdown') }}
                </h2>

                <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-7 h-7 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
</svg>
                </div>
              </div>
            </div>

            <div class="p-4 relative flex overflow-y-auto flex-1">
              <div class="overflow-x-auto absolute inset-0">
                <table class="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr class="child:border-b child:border-gray-200 sticky top-0 bg-white">
                      <th class="text-left p-2 font-semibold text-gray-700">
                        {{ $t('HeadContract.CostAggregationDashboard.section') }}
                      </th>

                      <th class="text-right p-2 font-semibold text-gray-700">
                        {{ $t('HeadContract.CostAggregationDashboard.tender') }}
                      </th>

                      <th class="text-right p-2 font-semibold text-gray-700">
                        {{ $t('HeadContract.CostAggregationDashboard.toDate') }}
                      </th>

                      <th class="text-right p-2 font-semibold text-gray-700">
                        {{ $t('HeadContract.CostAggregationDashboard.final') }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr class="hover:bg-gray-50 transition-colors child:border-b child:border-gray-100">
                      <td class="p-2 font-semibold text-gray-900">
                        {{ $t('HeadContract.CostAggregationDashboard.tradeCosts') }}
                      </td>

                      <td class="p-2 text-right text-gray-700">
                        {{ $tcc(costAggregationSummary.total_budget) }}
                      </td>

                      <td class="p-2 text-right text-gray-700">
                        {{ $tcc(tradeCertified) }}
                      </td>

                      <td class="p-2 text-right text-blue-600 font-semibold">
                        {{ $tcc(costAggregationSummary.total_budget + costAggregationSummary.variations.approved_variations_total) }}
                      </td>
                    </tr>

                    <tr
                      class="hover:bg-gray-50 transition-colors cursor-pointer child:border-b child:border-gray-100"
                      @click="openCosts = !openCosts"
                    >
                      <td class="p-2 font-semibold text-gray-900">
                        <div class="flex items-center space-x-2">
                          <button
                            :class="[{'rotate-90': openCosts}]"
                            class="transition-transform duration-200 p-1 rounded hover:bg-gray-100"
                            type="button"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 text-gray-600">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
                          </button>

                          <span>{{ $t('HeadContract.CostAggregationDashboard.indirectCosts') }}</span>
                        </div>
                      </td>

                      <td class="p-2 text-right text-gray-700">
                        {{ $tcc(costAggregationSummary.total_indirect_cost) }}
                      </td>

                      <td class="p-2 text-right text-gray-700">
                        {{ $tcc(costAggregationSummary.total_indirect_certified) }}
                      </td>

                      <td class="p-2 text-right text-blue-600">
                        {{ $tcc(costAggregationSummary.total_indirect_cost) }}
                      </td>
                    </tr>

                    <template v-if="openCosts">
                      <tr
                        v-for="trade in indirectTrades"
                        :key="trade.id"
                        class="hover:bg-gray-50 transition-colors child:border-b child:border-gray-100"
                      >
                        <td class="py-1 px-2 pl-6 text-gray-700">
                          {{ trade.name }}
                        </td>

                        <td class="py-1 px-2 text-right text-gray-600">
                          {{ $tcc(trade.totalBudget) }}
                        </td>

                        <td class="py-1 px-2 text-right text-gray-600">
                          {{ $tcc(trade.totalCertified) }}
                        </td>

                        <td class="py-1 px-2 text-right text-gray-600">
                          {{ $tcc(trade.totalBudget) }}
                        </td>
                      </tr>
                    </template>

                    <template v-if="hasSharedFunds">
                      <tr
                        class="hover:bg-gray-50 transition-colors cursor-pointer child:border-b child:border-gray-100"
                        @click="openSharedFunds = !openSharedFunds"
                      >
                        <td class="p-2 font-semibold text-gray-900">
                          <div class="flex items-center space-x-2">
                            <button
                              :class="[{'rotate-90': openSharedFunds}]"
                              class="transition-transform duration-200 p-1 rounded hover:bg-gray-100"
                              type="button"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 text-gray-600">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
                            </button>

                            <span>{{ $t('HeadContract.CostAggregationDashboard.sharedFunds') }}</span>
                          </div>
                        </td>

                        <td class="p-2 text-right text-gray-700">
                          {{ $tcc(costAggregationSummary.shared_funds.total_budget) }}
                        </td>

                        <td class="p-2 text-right text-gray-700">
                          {{ $tcc(costAggregationSummary.shared_funds.total_drawn) }}
                        </td>

                        <td class="p-2 text-right text-blue-600">
                          {{ $tcc(costAggregationSummary.shared_funds.total_budget) }}
                        </td>
                      </tr>

                      <template v-if="openSharedFunds">
                        <tr
                          v-for="fund in costAggregationSummary.shared_funds.funds"
                          :key="fund.id"
                          class="hover:bg-gray-50 transition-colors child:border-b child:border-gray-100"
                        >
                          <td class="py-1 px-2 pl-6 text-gray-700">
                            {{ fund.code }} - {{ fund.name }}
                          </td>

                          <td class="py-1 px-2 text-right text-gray-600">
                            {{ $tcc(fund.budget) }}
                          </td>

                          <td class="py-1 px-2 text-right text-gray-600">
                            {{ $tcc(fund.total_drawn) }}
                          </td>

                          <td class="py-1 px-2 text-right text-gray-600">
                            {{ $tcc(fund.budget) }}
                          </td>
                        </tr>
                      </template>
                    </template>

                    <tr class="bg-gradient-to-r from-blue-50 to-indigo-50 child:border-t child:border-blue-200 sticky bottom-0">
                      <td class="p-2 font-bold text-gray-900">
                        {{ $t('HeadContract.CostAggregationDashboard.total') }}
                      </td>

                      <td class="p-2 text-right font-bold text-gray-900">
                        {{ $tcc(costAggregationSummary.total_indirect_cost + costAggregationSummary.total_budget + costAggregationSummary.shared_funds.total_budget) }}
                      </td>

                      <td class="p-2 text-right font-bold text-gray-900">
                        {{ $tcc(costAggregationSummary.total_indirect_certified + costAggregationSummary.total_certified) }}
                      </td>

                      <td class="p-2 text-right font-bold text-blue-600">
                        {{ $tcc(costAggregationSummary.total_indirect_cost + costAggregationSummary.total_budget + costAggregationSummary.shared_funds.total_budget + costAggregationSummary.variations.approved_variations_total) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Contract Synopsis -->
          <div class="bg-white rounded-xl w-1/2">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-xl">
              <div class="flex items-center justify-between">
                <h2 class="font-semibold text-white">
                  {{ $t('HeadContract.CostAggregationDashboard.contractStatus') }}
                </h2>

                <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>
            </div>

            <div class="p-4">
              <div class="space-y-2">
                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.contractValue') }}</span>

                  <span class="font-semibold text-blue-600 text-sm">{{ $tcc(costAggregationSummary.total_budget) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.approvedVariationsValue') }}</span>

                  <span class="font-semibold text-gray-900 text-sm">{{ $tcc(costAggregationSummary.variations.approved_variations_total) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.approvedContractValue') }}</span>

                  <span class="font-semibold text-blue-600 text-sm">{{ $tcc(costAggregationSummary.total_budget + costAggregationSummary.variations.approved_variations_total) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.unapprovedVariationsValue') }}</span>

                  <span class="font-semibold text-gray-900 text-sm">{{ $tcc(costAggregationSummary.variations.unapproved_variations_total) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.estimatedFinalContractValue') }}</span>

                  <span class="font-semibold text-red-600 text-sm">{{ $tcc(costAggregationSummary.total_budget + costAggregationSummary.variations.unapproved_variations_total + costAggregationSummary.variations.approved_variations_total) }}</span>
                </div>

                <div class="flex justify-between items-center py-2">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.paymentsCertified') }}</span>

                  <span class="font-bold text-green-700 text-sm">{{ $tcc(costAggregationSummary.total_certified) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom Row - Chart and Variations -->
        <div class="flex w-full mt-2 space-x-2 h-1/2">
          <!-- Project Works Chart -->
          <ProjectWorksOverviewChart
            :cost-aggregation-summary="costAggregationSummary"
          />

          <!-- Variations Synopsis -->
          <div class="bg-white rounded-xl w-1/2">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-xl">
              <div class="flex items-center justify-between">
                <h2 class="font-semibold text-white">
                  {{ $t('HeadContract.CostAggregationDashboard.variationsSynopsis') }}
                </h2>

                <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>
            </div>

            <div class="p-4">
              <div class="space-y-2">
                <div class="flex justify-between items-center py-2 px-3 rounded">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.priced') }}</span>

                  <span class="font-semibold text-sm">{{ $tcc(costAggregationSummary.variations.priced) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 px-3 rounded">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.submitted') }}</span>

                  <span class="font-semibold text-sm">{{ $tcc(costAggregationSummary.variations.submitted) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 px-3 rounded">
                  <span class="text-gray-700 font-medium text-sm">Assessed</span>

                  <span class="font-semibold text-sm">{{ $tcc(costAggregationSummary.variations.provisioned) }}</span>
                </div>

                <div class="flex justify-between items-center py-2 px-3 rounded">
                  <span class="text-gray-700 font-medium text-sm">{{ $t('HeadContract.CostAggregationDashboard.approved') }}</span>

                  <span class="font-semibold text-sm">{{ $tcc(costAggregationSummary.variations.approved) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProjectWorksOverviewChart from '@/components/HeadContract/CostAggregation/ProjectWorksOverviewChart.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();
const tradesStore = useTradesStore();

const indirectTrades = computed(() => {
  return tradesStore.models
    .with('trade_items')
    .where('boq_type', 'indirect')
    .where('project_id', parseInt(props.projectId))
    .orderBy('sort_order')
    .get();
});

const costAggregationSummary = ref<CostAggregationSummary>({
  total_budget: 0,
  total_certified: 0,
  total_indirect_certified: 0,
  total_indirect_cost: 0,
  shared_funds: {
    total_budget: 0,
    total_drawn: 0,
    funds: [],
  },

  variations: {
    approved_variations_total: 0,
    unapproved_variations_total: 0,
    priced: 0,
    submitted: 0,
    provisioned: 0,
    approved: 0,
    total_variations: 0,
    total_budget: 0,
    completed_variations_total: 0,
    approved_count: 0,
    principled: 0,
    unapproved_count: 0,
  },
});

const hasSharedFunds = computed(() => costAggregationSummary.value.shared_funds.funds.length > 0);

// total_certified already includes shared fund drawdowns — exclude them from the trade line
const tradeCertified = computed(() => {
  return costAggregationSummary.value.total_certified - costAggregationSummary.value.shared_funds.total_drawn;
});

const costAggregationSummaryAction = useStoreApiAction(projectsStore.costAggregationSummary);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

useBoqLockRedirect();

watch(() => props.projectId, () => {
  costAggregationSummaryAction.request(props.projectId).then((costAggregation) => {
    costAggregationSummary.value = costAggregation;
  }).catch((error) => {
    console.log(error);
  });

  fetchTradesAction.request(props.projectId, { boq_type: 'indirect' });
}, { immediate: true });

const openCosts = ref(false);
const openSharedFunds = ref(false);
</script>

<style scoped></style>
